/**
 * Mẫu khai nghiệp vụ cho nguồn là SAP Business One.
 *
 * ── HAI LỚP, KHÔNG GỘP ────────────────────────────────────────────────────
 * Tệp này nói về LƯỢC ĐỒ SAP B1: bảng nào là gì, cột nào chứa gì, cái bẫy nào
 * đã nuốt người trước. Nó KHÔNG nói gì về chế độ kế toán — mã chỉ tiêu báo
 * cáo, dải tài khoản nguồn vốn thì ở `mau-ke-toan-vn.ts` (căn cứ TT 99/2025).
 *
 * Tách vì hai lớp có phạm vi khác nhau và đổi theo hai nhịp khác nhau: một
 * bản SAP B1 ở Singapore vẫn dùng `jdt1`/`oact` nhưng không theo thông tư nào
 * của Bộ Tài chính, còn một khách hàng Việt Nam trên Bravo hay Fast thì ngược
 * lại. Gộp chúng vào một mẫu là ép mọi người nhận cả hai để lấy một.
 *
 * Chèn được CẢ HAI cho một nguồn SAP B1 tại Việt Nam — chúng bổ sung nhau,
 * không chồng nhau.
 *
 * ── MỌI DÒNG "ĐÃ ĐO" ĐỀU LÀ ĐO THẬT ───────────────────────────────────────
 * Rút từ một phiên rà soát trên bản chạy `public_lbm` (SAP B1 + hệ thống tài
 * khoản TT200/TT99). Chỗ nào chưa đo được thì KHÔNG khẳng định — phần lớn
 * chúng là LỰA CHỌN CẤU HÌNH của từng khách hàng, và một mẫu khẳng định hộ sẽ
 * dạy sai cho bản cài kế tiếp. Chúng nằm ở `MAU_KIEM_SAP_B1`, hiện cho NGƯỜI
 * đọc ở hộp xác nhận chứ không ghi vào lời nhắc.
 */

export const MAU_SAP_B1_CAN_CU = 'SAP Business One';

/** Bảng chỉ dấu. Có đủ ba bảng này thì gần như chắc chắn là SAP B1. */
const DAU_HIEU = ['jdt1', 'ojdt', 'oact'];

/**
 * Nguồn này có phải SAP B1 không, đoán theo tên bảng ĐÃ KHAI.
 *
 * Dùng để GỢI Ý nút, không để tự áp: đoán sai thì người dùng thấy một nút
 * thừa, còn tự áp sai thì bản khai mang lời khẳng định về một lược đồ không
 * tồn tại — và lời khai sai hỏng âm thầm.
 */
export function laSapB1(tenBang: string[]): boolean {
  const co = new Set(tenBang.map((x) => x.trim().toLowerCase()));
  return DAU_HIEU.every((x) => co.has(x));
}

export const MAU_GHI_CHU_SAP_B1 = `LƯỢC ĐỒ SAP BUSINESS ONE

Tên cột trong bản khai là snake_case, nền tảng tự dịch sang tên gốc của SQL
Server khi chạy. Viết \`acct_code\`, \`father_num\`, \`ref_date\` — không viết
\`AcctCode\`, \`FatherNum\`, \`RefDate\`.

Bảng chính: ojdt chứng từ bút toán (ref_date là ngày hạch toán) · jdt1 dòng
bút toán (account, debit, credit, trans_id) · oact hệ thống tài khoản
(acct_code, acct_name, father_num) · ocrd đối tác · oitm mặt hàng.
Quy ước SAP: bảng O… là đầu chứng từ, bảng …1 là dòng chi tiết, nối nhau bằng
doc_entry (riêng bút toán là trans_id).

BA CÁI BẪY ĐÃ ĐO ĐƯỢC
1. oact.father_num KHÔNG phải chữ số cấp 1. Nó là MÃ TÀI KHOẢN CHA, có đệm số
   0: cha của 331 là '200000000000000', cha của 3311 là '331'. Lọc bằng
   father_num IN ('3','4') khớp KHÔNG dòng nào và trả về một dòng NULL trông
   như "chưa có số liệu". Lọc nhóm bằng LEFT(jdt1.account, 1).
2. jdt1.short_name KHÔNG phải tên đơn vị. Nó chứa mã tài khoản hoặc mã đối
   tác (152, 6272, NC00224, KH05599). LIKE '%TÊN ĐƠN VỊ%' trên cột này không
   bao giờ khớp.
3. Ngăn tài khoản mặc định của SAP mang TÊN TIẾNG ANH và nằm chồng lên hệ
   thống tài khoản Việt: 100000000000000 Assets · 200000000000000 Liabilities
   · 300000000000000 Capital and Reserves · 400000000000000 Turnover. Chúng là
   tài khoản tiêu đề, không hạch toán. Đừng nhầm số 3 và 4 của NGĂN với chữ số
   đầu của MÃ TÀI KHOẢN Việt Nam — nợ phải trả Việt Nam (331, 333…) nằm trong
   ngăn 2, không phải ngăn 3.

CHIỀU PHÂN TÍCH CÓ THỂ KHÔNG CÓ
profit_code và ocr_code4 thường CHỈ được điền trên tài khoản chi phí. Nếu
chúng trống trên tài khoản loại 3 và 4 thì "theo đơn vị" là câu hỏi không trả
lời được từ jdt1 — hãy nói ra điều đó thay vì trả về 0.`;

/**
 * Bốn câu người vận hành nên chạy sau khi khai — KHÔNG ghi vào bản khai.
 *
 * ── VÌ SAO KHÔNG ĐỂ CHUNG VỚI GHI CHÚ ─────────────────────────────────────
 * Bản đầu nhét cả bốn câu này vào `MAU_GHI_CHU_SAP_B1`, và đó là một lỗi
 * thiết kế chứ không chỉ một lỗi độ dài: ghi chú đi vào lời nhắc của MỌI câu
 * hỏi trên nguồn, còn bốn câu này là việc của CON NGƯỜI, làm đúng một lần.
 * Mô hình không chạy được chúng và không cần đọc chúng — chúng chỉ ngồi đó
 * ăn token, vĩnh viễn.
 *
 * Triệu chứng lộ ra ở trần 4.000 ký tự: hai mẫu ghép lại thành 4.945 và máy
 * chủ trả 422. Nâng trần sẽ giấu lỗi đi thay vì chữa nó.
 *
 * Hiện ở hộp xác nhận, đúng lúc người vận hành đang nhìn và sắp hành động.
 */
export const MAU_KIEM_SAP_B1 = `Bốn điểm dưới đây là LỰA CHỌN CẤU HÌNH, khác nhau giữa các bản cài — đừng giả
định, hãy chạy thử ở màn Thử nghiệm rồi ghi kết quả vào ghi chú.

1) Chiều phân tích có được điền trên tài khoản bảng cân đối không?
   SELECT DISTINCT TOP 50 LEFT(j.account,1) AS lop, j.profit_code, j.ocr_code4
   FROM jdt1 AS j WHERE LEFT(j.account,1) IN ('3','4')

2) Một CSDL là một đơn vị, hay nhiều đơn vị chung một CSDL?
   Nếu mỗi đơn vị một CSDL thì "đơn vị" là cách CHỌN NGUỒN, không phải một
   điều kiện WHERE.
   SELECT DISTINCT TOP 50 profit_code, ocr_code4, short_name FROM jdt1

3) Có bảng báo cáo tính sẵn không? Nhiều bản cài có bảng riêng cho bảng cân
   đối, thường mang tiền tố của đơn vị triển khai. Có thì DÙNG NÓ thay vì
   dựng lại từ jdt1.
   SELECT TOP 50 name FROM sys.tables ORDER BY name

4) Sổ đã kết chuyển chưa? Lớp 5–9 tổng bằng 0 nghĩa là lãi lỗ đã về 421; khác
   0 nghĩa là số dư nguồn vốn CHƯA gồm lãi trong kỳ.
   SELECT LEFT(j.account,1) AS lop, COALESCE(SUM(j.credit - j.debit),0) AS so_du
   FROM jdt1 AS j INNER JOIN ojdt AS o ON j.trans_id = o.trans_id
   GROUP BY LEFT(j.account,1) ORDER BY lop`;

export const MAU_THUAT_NGU_SAP_B1: { term: string; meaning: string }[] = [
  {
    term: 'bút toán',
    meaning:
      'Đầu chứng từ ở bảng ojdt (ref_date là ngày hạch toán), dòng chi tiết ở bảng ' +
      'jdt1, nối nhau bằng trans_id. jdt1.account là mã tài khoản, debit và credit ' +
      'là phát sinh Nợ và Có.',
  },
  {
    term: 'tài khoản kế toán',
    meaning:
      'Bảng oact: acct_code là mã, acct_name là tên. father_num là mã tài khoản CHA ' +
      'có đệm số 0, KHÔNG phải chữ số cấp 1 — lọc nhóm bằng LEFT(jdt1.account, 1).',
  },
];
